import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { View, Image } from 'react-native-web';

import { HeaderText } from '../../../ui/HeaderText';
import BodyText from '../../../ui/BodyText';
import OffsetBorderBox from '../../../ui/OffsetBorderBox';

import AvatarIcon from '../../../AvatarIcon';
import StateLink from '../../../StateLink';

import HoorayIcon from '../../../../../images/icons/hooray.svg';
import LightbulbIcon from '../../../../../images/icons/icon-lightbulb.svg';

import './NewClientDeliveryDisplay.module.scss';

export default function NewClientDeliveryDisplay({
  appointmentId,
  clientName,
  clientThumbnail,
  serviceName,
  ncdFeePercent,
  responses,
  localStart,
  clickTracking,
  appearance,
}) {
  const [firstName] = clientName.split(' ');
  const localStartMoment = moment(localStart);
  const hasResponses = responses.length > 0;
  return (
    <StateLink
      to="provider.appointment"
      params={{ viewApptId: appointmentId }}
      clickTracking={clickTracking}
    >
      <View styleName={classNames('ncd-display', `ncd-display-${appearance}`)} testID="ncd-insights-notification">
        {appearance === 'notification' && (
          <View>
            <View>
              <AvatarIcon
                icon={clientThumbnail}
                name={clientName}
              />
            </View>
          </View>
        )}
        <View styleName={classNames('display-body', `display-body-${appearance}`)}>
          <View styleName="display-body-top">
            <Image styleName="ncd-icon" source={HoorayIcon} />
            <View styleName="info-wrapper">
              <BodyText styleName="display-body-top-text">
                We brought you a new client!
              </BodyText>
              <BodyText styleName="service-name">
                <BodyText styleName="service-name" emphasize>
                  {serviceName}{' '}
                </BodyText>
                for {localStartMoment.format('dddd, MMM\u00A0D')}{' '}
                at {localStartMoment.format('h:mm\u00A0A')}
              </BodyText>
            </View>
          </View>
          <View styleName={classNames('client-details-box', `client-details-box-${appearance}`)}>
            <OffsetBorderBox>
              <View styleName="details-content">
                <View styleName="client-name-container">
                  <HeaderText styleName={classNames('client-name', { 'client-name-underline': hasResponses })} level="3">
                    {clientName}
                  </HeaderText>
                </View>
                {hasResponses ? (
                  <React.Fragment>
                    <BodyText styleName="tips">
                      Tips to make {firstName} a regular:
                    </BodyText>
                    <View styleName="client-insight">
                      <Image styleName="lightbulb-icon" source={LightbulbIcon} />
                      <BodyText styleName="insight-text">Client Insight</BodyText>
                    </View>
                    <View styleName="response-list">
                      {responses.map(response => (
                        <BodyText styleName="response" key={response.sequence}>{response.answer_pro_display_text}</BodyText>
                      ))}
                    </View>
                  </React.Fragment>
                ) : (
                  <BodyText styleName="no-responses">
                    We invested in marketing to help you get booked by new
                    clients looking for long-term professionals.
                  </BodyText>
                )}
              </View>
            </OffsetBorderBox>
            {hasResponses && (
              <BodyText styleName="disclaimer">
                We&apos;ll keep {ncdFeePercent}% of the first appointment
                and invest it back into marketing for you
              </BodyText>
            )}
          </View>
        </View>
      </View>
    </StateLink>
  );
}

NewClientDeliveryDisplay.propTypes = {
  appointmentId: PropTypes.number.isRequired,
  clientName: PropTypes.string.isRequired,
  clientThumbnail: PropTypes.string,
  serviceName: PropTypes.string.isRequired,
  ncdFeePercent: PropTypes.number.isRequired,
  responses: PropTypes.array,
  localStart: PropTypes.string.isRequired,
  clickTracking: PropTypes.object,
  appearance: PropTypes.oneOf([
    'notification',
    'note',
  ]).isRequired,
};

NewClientDeliveryDisplay.defaultProps = {
  responses: [],
  clientThumbnail: '',
  clickTracking: undefined,
};
